import { pxToDP } from '@capsicum/common';
import { InteractionLayer } from '@capsicum/common-web';
import { type SelectFavouriteTeamSelectionVariants } from '@capsicum/socios-core';
import { useCallback } from 'react';
import styled, { css } from 'styled-components';

import { Team } from './Team';

type SelectorTeamProps = {
	disabled?: boolean;
	image?: string;
	isSmall?: boolean;
	name: string;
	onClick: (partnerID: string) => void;
	partnerID: string;
	selection?: SelectFavouriteTeamSelectionVariants;
	width: number;
};

export const SelectorTeam = ({
	disabled = false,
	image,
	isSmall = false,
	name,
	onClick: onClickProp,
	partnerID,
	selection,
	width
}: SelectorTeamProps) => {
	const isSelected = !!selection;

	const onClick = useCallback(() => {
		if (disabled) return;
		onClickProp(partnerID);
	}, [onClickProp, partnerID, disabled]);

	return (
		<S.Container
			$disabled={disabled}
			$selected={isSelected}
			$width={width}
			key={partnerID}
			testID={`selectFavouriteTeam-partner-${name}`}
			onClick={onClick}
		>
			<S.StyledTeam
				$selected={isSelected}
				disabled={disabled}
				image={image}
				isFavourite={selection === 'favourite' && isSelected}
				isSmall={isSmall}
				name={name}
			/>
		</S.Container>
	);
};

SelectorTeam.HEIGHT = 126;

namespace S {
	export const Container = styled(InteractionLayer)<{ $disabled: boolean; $selected: boolean; $width: number }>`
		overflow: hidden;
		user-select: none;
		border-style: solid;
		height: ${SelectorTeam.HEIGHT}px;
		width: ${({ $width }) => $width}px;
		${({ theme }) => theme.border.radius.medium};

		${({ $disabled }) =>
			$disabled &&
			css`
				pointer-events: none;
			`}

		border-width: ${({ $disabled, $selected, theme }) =>
			!$selected || $disabled ? theme.border.width.small : theme.border.width.large};

		border-color: ${({ $disabled, $selected, theme }) =>
			$disabled
				? theme.colors.borders.main.disabled
				: $selected
					? theme.colors.borders.brand.selected
					: theme.colors.borders.main.subtle};

		${({ $width }) =>
			$width === 0 &&
			css`
				opacity: 0;
			`};
	`;

	export const StyledTeam = styled(Team)<{ $selected: boolean }>`
		width: 100%;
		height: 100%;
		background: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ $selected, theme }) =>
			$selected
				? `${theme.spacings.xsmall}`
				: pxToDP(theme.spacings.xsmall) + pxToDP(theme.border.width.small) + 'px'};
		padding-bottom: 0;
	`;
}
